import { zodResolver } from '@hookform/resolvers/zod';
import { SUBJECT_TAGS_ACCOUNTS_PAYABLE, Transaction } from '@kalos/kalos-rpc';
import { toast } from '@kalos/ui';
import { format, isAfter, isValid, parseISO } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import {
  type MostLikelyJobRequestFilter,
  useEventQueryInline,
} from '../../../hooks/react-query/useEventsQuery';
import { useTimesheetDepartmentListQuery } from '../../../hooks/react-query/useTimesheetDepartmentQuery';
import { useTransactionImmediateGetQuery } from '../../../hooks/react-query/useTransactionQuery';
import { useBatchUserQuery } from '../../../hooks/react-query/useUserQuery';
import { isMoneyValue } from '../../../tools/helpers';
import { castToNumber } from '../../../tools/schemas';
import { type ZodObjectWithModelKeys } from '../../../tools/typeguargs';
import { getFieldMaskFromDirtyField } from '../EmployeeDepartments/utils';
import { techniciansUserFilter } from '../Pickers/newPickers/utils';
import { type FormAlert } from '../TransactionTable/components/FormAlertArea';
import {
  clearDuplicateInvoiceAlert,
  getDuplicateInvoiceAlert,
  getDuplicateInvoiceFilter,
  VERIFY_INVOICE_FAILED_ALERT,
} from '../TransactionTable/components/utils/duplicateInvoice';

/**
 * The Edit Transaction form's schema, defaults, options and save, shared by the desktop form
 * (EditTransaction) and the phone page (EditTransactionPagePhone) so both validate and save alike.
 */

type TransactionEditForm = Pick<
  Transaction,
  | 'jobId'
  | 'departmentId'
  | 'ownerId'
  | 'costCenterId'
  | 'amount'
  | 'assignedEmployeeId'
  | 'timestamp'
  | 'notes'
  | 'description'
  | 'vendorId'
  | 'orderNumber'
  | 'invoiceNumber'
  | 'vendorCategory'
  | 'paymentDueDate'
>;

const MINIMAL_YEAR_DATE = new Date(2000, 0, 1);

export const tagsTranslated: { label: string; value: string }[] =
  SUBJECT_TAGS_ACCOUNTS_PAYABLE.filter((tag) => tag.value !== '0').map((tag) => ({
    label: tag.label,
    value: tag.label.replace(' ', ''),
  }));

export const transactionEditScheme = z.object({
  timestamp: z
    .date()
    .default(() => new Date())
    .refine((date) => isValid(date), 'Not a valid date')
    .refine((date) => isAfter(date, MINIMAL_YEAR_DATE), 'Date should be after 01/01/2000'),
  orderNumber: z.string().optional(),
  invoiceNumber: z.string().optional().default(''),
  ownerId: castToNumber.optional().default(0),
  departmentId: castToNumber.default(0),
  jobId: z.number().optional().default(0),
  costCenterId: castToNumber.optional().default(0),
  // refine is to make sure that number has only 2 digits after comma
  amount: castToNumber
    .optional()
    .default(0)
    .refine(isMoneyValue, 'Amount should have only 2 digits after comma'),
  notes: z.string().optional().default(''),
  assignedEmployeeId: castToNumber.optional().default(0),
  description: z.string().optional().default(''),
  vendorId: castToNumber,
  vendorCategory: z
    .string()
    .refine(
      (val) => tagsTranslated.some((tag) => tag.value !== '0' && tag.value === val),
      'Select vendor category',
    ),
  paymentDueDate: z.string().optional().default(''),
}) satisfies ZodObjectWithModelKeys<TransactionEditForm>;

export type TransactionEditSchemaType = z.infer<typeof transactionEditScheme>;

export const getDefaultTransactionEditFormValues: (
  txn: Transaction,
) => TransactionEditSchemaType = (txn) => {
  const parsedTime = parseISO(txn.timestamp);

  return {
    timestamp: isValid(parsedTime) ? parsedTime : new Date(),
    orderNumber: txn.orderNumber ?? '',
    invoiceNumber: txn.invoiceNumber ?? '',
    ownerId: txn.ownerId ?? 0,
    departmentId: txn.departmentId ?? 0,
    jobId: txn.jobId ?? 0,
    costCenterId: txn.costCenterId ?? 0,
    amount: txn.amount ?? 0,
    notes: txn.notes ?? '',
    assignedEmployeeId: txn.assignedEmployeeId ?? 0,
    description: txn.description ?? '',
    vendorId: txn.vendorId,
    vendorCategory: txn.vendorCategory ?? '0',
    paymentDueDate: txn.paymentDueDate ?? format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
  } satisfies TransactionEditSchemaType;
};

/** The latest date the date pickers allow: a year from today. */
export const getEditTransactionMaxDate = () => {
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 1);
  return maxDate;
};

const useTransactionEditForm = ({
  defaultValues,
  disabled,
}: {
  defaultValues: TransactionEditSchemaType;
  disabled?: boolean;
}) => {
  return useForm<TransactionEditSchemaType>({
    resolver: zodResolver(transactionEditScheme),
    mode: 'onChange',
    defaultValues: defaultValues,
    disabled,
  });
};

/**
 * The form, its option lists, the duplicate invoice check, the department that follows the job,
 * and the save that builds the edited Transaction (with its field mask) for `onSave`.
 */
export const useEditTransactionForm = ({
  transactionInput,
  disabled,
  onSave,
}: {
  transactionInput: Transaction;
  disabled?: boolean;
  onSave: (saved: Transaction) => void;
}) => {
  const form = useTransactionEditForm({
    defaultValues: useMemo(
      () => getDefaultTransactionEditFormValues(transactionInput),
      [transactionInput],
    ),
    disabled,
  });

  const jobId = useWatch({ control: form.control, name: 'jobId' });
  const vendorId = useWatch({ control: form.control, name: 'vendorId' });
  const jobSelectorHint = useMemo<MostLikelyJobRequestFilter>(
    () => ({
      targetDate: transactionInput.timestamp,
      userId: transactionInput.ownerId,
    }),
    [transactionInput.ownerId, transactionInput.timestamp],
  );

  const timesheetDepartmentQuery = useTimesheetDepartmentListQuery({
    filter: { isActive: 1, teamOnly: false, fieldMask: ['TeamOnly'] },
    select(data) {
      return (
        data?.results.map((department) => ({
          label: `${department.description}`,
          value: department.id.toString(),
        })) ?? []
      );
    },
  });

  const techniciansQuery = useBatchUserQuery({
    filters: techniciansUserFilter,
    select(data) {
      return data.results.map((technician) => ({
        label: `${technician.firstname} ${technician.lastname} (ID: ${technician.id})`,
        value: technician.id.toString(),
      }));
    },
  });

  const eventQuery = useEventQueryInline();
  const transactionGetQuery = useTransactionImmediateGetQuery();
  const [formAlert, setFormAlert] = useState<FormAlert | null>(null);

  const findDuplicateInvoice = useCallback(
    async (invoiceNumber: string, vendorIdToCheck: number) => {
      const filter = getDuplicateInvoiceFilter(invoiceNumber, vendorIdToCheck, transactionInput.id);
      if (!filter) return null;
      const result = await transactionGetQuery(filter);
      return result?.id && result.id !== transactionInput.id ? result : null;
    },
    [transactionGetQuery, transactionInput.id],
  );

  const handleCheckInvoiceNumber = useCallback(
    async (invoiceNumber: string) => {
      try {
        const duplicate = await findDuplicateInvoice(invoiceNumber, vendorId);
        setFormAlert(
          duplicate ? getDuplicateInvoiceAlert(invoiceNumber) : clearDuplicateInvoiceAlert,
        );
      } catch (err) {
        console.error('Failed to check Invoice number', err);
      }
    },
    [findDuplicateInvoice, vendorId],
  );

  const handleSave: SubmitHandler<TransactionEditSchemaType> = async (data) => {
    try {
      const duplicate = await findDuplicateInvoice(data.invoiceNumber, data.vendorId);
      if (duplicate) {
        setFormAlert(getDuplicateInvoiceAlert(data.invoiceNumber));
        return;
      }
      setFormAlert(clearDuplicateInvoiceAlert);
    } catch (err) {
      console.error('Failed to check Invoice number', err);
      setFormAlert(VERIFY_INVOICE_FAILED_ALERT);
      return;
    }
    const fieldMask = getFieldMaskFromDirtyField(form.formState.dirtyFields).map((fieldName) =>
      fieldName === 'vendorId' ? 'Vendor' : fieldName,
    );
    const modifiedTransaction = Transaction.create({
      ...transactionInput,
      ...data,
      fieldMask,
      timestamp: format(data.timestamp, 'yyyy-MM-dd HH:mm:ss'),
    });
    onSave(modifiedTransaction);
  };

  const handleSetDepartment = useCallback(
    async (id: number) => {
      if (id === 0) return;

      try {
        const event = await eventQuery.fetchEvent({
          filter: {
            id,
          },
        });

        if (event?.departmentId && event.departmentId !== form.getValues('departmentId')) {
          form.setValue('departmentId', event.departmentId, {
            shouldDirty: true,
          });
        }
      } catch (error) {
        console.error('Failed to fetch job or update department:', error);
        toast({
          variant: 'destructive',
          title: 'Error updating department',
          description: 'Failed to fetch job details. Please try again.',
        });
      }
    },
    [eventQuery, form],
  );

  return {
    form,
    jobId,
    jobSelectorHint,
    timesheetDepartmentQuery,
    techniciansQuery,
    formAlert,
    handleCheckInvoiceNumber,
    handleSave,
    handleSetDepartment,
  };
};
